import { createContext, useContext, useEffect, useRef, useState } from "react";
import { trackAudioUrl, api } from "../lib/api";

const PlayerContext = createContext(null);

export const PlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = new Audio();
  const playToken = useRef(0); // anti-course : seul le dernier play() compte
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false); // mise en mémoire tampon en cours
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    // L'état reflète la RÉALITÉ de l'élément audio (pas l'intention du clic)
    const onTime = () => setProgress(a.currentTime);
    const onMeta = () => setDuration(a.duration || 0);
    const onEnd = () => setPlaying(false);
    const onPlaying = () => { setPlaying(true); setLoading(false); };
    const onWaiting = () => setLoading(true);
    const onError = () => { setPlaying(false); setLoading(false); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    a.addEventListener("playing", onPlaying);
    a.addEventListener("waiting", onWaiting);
    a.addEventListener("error", onError);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("playing", onPlaying);
      a.removeEventListener("waiting", onWaiting);
      a.removeEventListener("error", onError);
    };
  }, []);

  const playTrack = (track) => {
    const a = audioRef.current;
    if (current?.track_id === track.track_id) {
      toggle();
      return;
    }
    const token = ++playToken.current;
    a.pause();
    setCurrent(track);
    setPlaying(false);
    setLoading(true);
    setProgress(0);
    setDuration(0);
    a.src = trackAudioUrl(track);
    a.play().catch(() => {
      // rejeté (changement de morceau, politique autoplay…) : on n'allume l'UI que si c'est encore le dernier appel
      if (playToken.current === token) { setPlaying(false); setLoading(false); }
    });
    api.post(`/tracks/${track.track_id}/play`).catch(() => {});
  };

  const toggle = () => {
    if (!current) return;
    const a = audioRef.current;
    if (playing || loading) {
      a.pause();
      setPlaying(false);
      setLoading(false);
    } else {
      const token = ++playToken.current;
      setLoading(true);
      a.play().catch(() => {
        if (playToken.current === token) { setPlaying(false); setLoading(false); }
      });
    }
  };

  const seek = (frac) => {
    const a = audioRef.current;
    if (duration && isFinite(duration)) a.currentTime = frac * duration;
  };

  const close = () => {
    audioRef.current.pause();
    setCurrent(null);
    setPlaying(false);
    setLoading(false);
  };

  return (
    <PlayerContext.Provider
      value={{ current, playing, loading, progress, duration, playTrack, toggle, seek, close }}
    >
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => useContext(PlayerContext);
